import { ApiError } from "../../api/client";
import { lang, t } from "../../i18n/i18n";
import { formatBytes } from "../../map/zones/storage";
import { authDate, authTime } from "../authText";
import type { AccountStatus, AdminRequestRow, Eligibility, RequestKind, UnitAccount } from "./adminTypes";

/** Textes de l'administration des comptes (docs/17 §8.3, « Comptes de l'unité ») : états, rôles, gestes des
 *  demandes, refus du serveur rendus par leur code, tailles. */

export function statusLabel(account: Pick<UnitAccount, "status" | "delayed_until">): string {
  if (account.status === "active" && account.delayed_until)
    return t("En attente jusqu'à {heure}", { heure: authTime(account.delayed_until) });
  const labels: Record<AccountStatus, string> = {
    active: t("Actif"),
    pending: t("En attente d'activation"),
    locked: t("Verrouillé"),
    disabled: t("Désactivé"),
  };
  return labels[account.status];
}

export function roleLabel(role: string): string {
  return role === "admin" ? t("Administrateur") : t("Utilisateur");
}

/** Geste d'une demande à deux administrateurs, en clair. */
export function requestAction(kind: RequestKind): string {
  switch (kind) {
    case "reset":
      return t("Code de réinitialisation");
    case "promote":
      return t("Nommer administrateur");
    case "demote":
      return t("Retirer le rôle d'administrateur");
    case "deactivate":
      return t("Désactiver le compte");
  }
}

/** Pourquoi l'administrateur qui lit la demande ne peut pas l'approuver. */
export function eligibilityText(reason: Eligibility, from: string | null): string | null {
  switch (reason) {
    case "too_recent":
      return t("Vous êtes administrateur depuis moins de 7 jours : vous pourrez approuver une demande à partir du {date}.", {
        date: authDate(from),
      });
    case "same_seat_code":
      return t("Votre code d'activation a servi sur le poste d'où la demande a été déposée : un autre administrateur doit l'approuver.");
    case "recovered":
      return t("Votre compte a été repris par le code de secours il y a moins de 7 jours : vous pourrez approuver une demande à partir du {date}.", {
        date: authDate(from),
      });
    case "self":
      return t("Vous avez déposé cette demande : un autre administrateur doit l'approuver.");
    case "target":
      return t("Cette demande vous concerne : un autre administrateur doit l'approuver.");
    case "not_admin":
      return t("Seul un administrateur actif de l'unité peut approuver une demande.");
    default:
      return null;
  }
}

/** Nomination d'un compte dont le demandeur a émis le dernier code : il a pu s'en servir lui-même. */
export function targetCodeText(row: AdminRequestRow): string | null {
  const code = row.target_code;
  if (!code) return null;
  const values = {
    demandeur: row.requested_by.display_name ?? "—",
    nom: row.target.display_name ?? "—",
    date: authDate(code.issued_at),
  };
  return code.kind === "activation"
    ? t("{demandeur} a émis le code d'activation de {nom} le {date} : avant d'approuver, vérifiez auprès de cette personne qu'elle a bien utilisé ce code elle-même.", values)
    : t("{demandeur} a émis un code de réinitialisation pour {nom} le {date} : avant d'approuver, vérifiez auprès de cette personne qu'elle a bien utilisé ce code elle-même.", values);
}

/** Texte d'un refus du serveur dans les vues d'administration (codes du §7.8), sinon son message traduit. */
export function adminErrorText(error: unknown): string {
  if (!(error instanceof ApiError)) return t("Action impossible pour l'instant. Réessayez.");
  switch (error.code) {
    case "LAST_ADMIN":
      return t("L'unité doit garder au moins un administrateur actif : nommez-en un autre d'abord.");
    case "ADMIN_BY_COMMAND":
      return t("Le deuxième administrateur de l'unité est nommé par BELLICOM, après vérification de son identité : demandez-le à BELLICOM en donnant l'identifiant de ce compte.");
    case "LOGIN_TAKEN":
      return t("Identifiant déjà pris");
    case "APPROVER_NOT_ELIGIBLE":
      return eligibilityText((error.detail?.reason as Eligibility) ?? null, null) ?? error.message;
    case "ACCOUNT_CHANGED":
      return t("Ce compte a été modifié entre-temps : la fiche a été relue, recommencez.");
    case "LICENCE_READ_ONLY":
      return t("Licence échue : seuls quelques gestes d'administration restent permis pendant la semaine de lecture seule.");
    default:
      return error.message || t("Action impossible pour l'instant. Réessayez.");
  }
}

/** Taille en unités décimales, comme la jauge de la licence (« 820 Ko », « 1,2 Go ») ; « — » pour rien. */
export function sizeLabel(bytes: number): string {
  return bytes > 0 ? formatBytes(bytes, lang()) : "—";
}

/** Plafond de stockage par compte, en clair (« Aucun », « 250 Mo »…). */
export function quotaLabel(bytes: number | null): string {
  return bytes === null ? t("Aucun") : formatBytes(bytes, lang());
}

/** Délai de verrouillage en clair (« 15 min », « 1 h »). */
export function lockLabel(minutes: number): string {
  return minutes >= 60 ? t("{n} h", { n: minutes / 60 }) : t("{n} min", { n: minutes });
}

/** Cellule CSV : neutralisée si elle commence comme une formule de tableur (nom de poste choisi à
 *  l'activation, « =HYPERLINK(…) »), puis entre guillemets si besoin. */
export function csvCell(raw: string): string {
  const value = /^[=+\-@\t\r]/.test(raw) ? `'${raw}` : raw;
  return /[";\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}
